import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { companiesApi } from '../api/companies';
import { useAuth } from './AuthContext';
import { queryClient } from '../main';
import type { Company } from '../types';

interface CompanyContextValue {
  currentCompany: Company | null;
  userCompanies: Company[];
  isLoading: boolean;
  switchCompany: (uuid: string) => void;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [userCompanies, setUserCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  // Starts true: on the very first render the companies have not been asked
  // for yet, and "not loading, no companies" is indistinguishable from
  // "loaded, genuinely no companies" — which sent staff opening a ticket
  // link straight to the customer portal before the fetch even began.
  const [isLoading, setIsLoading] = useState(true);

  const loadCompanies = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const companies = await companiesApi.list();
      setUserCompanies(companies);

      // Restore last selected company or pick first (API returns id, not uuid)
      const savedId = localStorage.getItem('ctticket-company') || localStorage.getItem('currentCompanyId');
      const companyId = (c: Company & { id?: string }) => (c as any).id ?? (c as any).uuid;
      const saved = savedId ? companies.find(c => companyId(c) === savedId) : null;
      const active = saved || companies[0] || null;

      if (active) {
        setCurrentCompany(active);
        const id = companyId(active);
        localStorage.setItem('ctticket-company', id);
        import('../api/client').then(({ apiClient, tokenManager }) => {
          apiClient.defaults.headers.common['X-Company-Id'] = id;
          tokenManager.setCompanyId(id);
        });
      }
    } catch {
      setUserCompanies([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCompanies();
    } else {
      setUserCompanies([]);
      setCurrentCompany(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, loadCompanies]);

  const companyId = (c: Company & { id?: string }) => (c as any).id ?? (c as any).uuid;
  const switchCompany = useCallback((id: string) => {
    const company = userCompanies.find(c => companyId(c) === id);
    if (company) {
      // Clear all cached query data before switching to prevent cross-tenant bleed
      queryClient.clear();
      setCurrentCompany(company);
      localStorage.setItem('ctticket-company', id);
      import('../api/client').then(({ apiClient, tokenManager }) => {
        apiClient.defaults.headers.common['X-Company-Id'] = id;
        tokenManager.setCompanyId(id);
      });
    }
  }, [userCompanies]);

  const refreshCompanies = useCallback(async () => {
    await loadCompanies();
  }, [loadCompanies]);

  return (
    <CompanyContext.Provider
      value={{ currentCompany, userCompanies, isLoading, switchCompany, refreshCompanies }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
